import { apiClient } from "@/services/api";
import { logError } from "@/lib/error-handling";
import { ContactDto, ContactWithAuditDto } from "@/types";
import { EditContactFormData } from "@/components/forms/edit-contact-form";
import { StakeholderRole } from "@/enums/contact.enum";

export interface ContactFormData {
  name: string;
  lastname: string;
  email: string;
  jobTitle: string;
  stakeholderRoles: StakeholderRole[];
  department: string;
  accountId: string; // Required for creating contact for a specific account
  // Optional field for UpdateContactForm
  linkedin?: string;
  openToBetas?: boolean;
  openToTestimonial?: boolean;
}

export interface ContactApiData extends Omit<ContactFormData, "accountId"> {
  account_id: string;
}

export function mapFormDataToApiData(
  formData: ContactFormData
): ContactApiData {
  return {
    name: formData.name,
    lastname: formData.lastname,
    email: formData.email,
    jobTitle: formData.jobTitle,
    stakeholderRoles: formData.stakeholderRoles,
    department: formData.department,
    account_id: formData.accountId,
    linkedin: formData.linkedin,
    openToBetas: formData.openToBetas,
    openToTestimonial: formData.openToTestimonial,
  };
}

/**
 * Creates a new contact by sending the provided data to the backend.
 *
 * @param accountId - The ID of the account to associate the contact with.
 * @param data - The data required to create a new contact.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createContact(
  accountId: string,
  data: ContactApiData
): Promise<ContactWithAuditDto> {
  try {
    return await apiClient<ContactWithAuditDto>(
      `/api/accounts/${accountId}/contacts`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "createContact");
    throw error;
  }
}

/**
 * Fetches all contacts for a specific account.
 *
 * @param accountId - The ID of the account to fetch contacts for
 * @returns A list of contacts.
 * @throws An error if the request fails.
 */
export async function fetchContactsByAccountId(
  accountId: string
): Promise<ContactDto[]> {
  try {
    return await apiClient<ContactDto[]>(
      `/api/accounts/${accountId}/contacts`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchContactsByAccountId");
    throw error;
  }
}

/**
 * Fetches the top 2 most active contacts for a specific account.
 * Prioritizes contacts with activity by lastActivityDate, then falls back to most recently created contacts.
 *
 * @param accountId - The ID of the account to fetch top active contacts for
 * @returns A list of top 2 most active contacts.
 * @throws An error if the request fails.
 */
export async function fetchTopActiveContactsByAccountId(
  accountId: string
): Promise<ContactDto[]> {
  try {
    return await apiClient<ContactDto[]>(
      `/api/accounts/${accountId}/contacts?keyContacts=true`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchTopActiveContactsByAccountId");
    throw error;
  }
}

/**
 * Fetches a contact by its ID.
 * @param contactId - The ID of the contact to fetch
 * @returns A promise that resolves with the contact data.
 * @throws An error if the request fails or if the contact is not found.
 */
export async function fetchContactById(
  contactId: string
): Promise<ContactWithAuditDto> {
  try {
    return await apiClient<ContactWithAuditDto>(`/api/contacts/${contactId}`, {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchContactById");
    throw error;
  }
}

/**
 * Updates an existing contact by its ID.
 * @param contactId - The ID of the contact to update
 * @param data - The data to update the contact with
 * @returns A promise that resolves with the updated contact data.
 * @throws An error if the request fails or if the contact is not found.
 */
export async function updateContact(
  contactId: string,
  data: Partial<EditContactFormData>
): Promise<ContactWithAuditDto> {
  try {
    return await apiClient<ContactWithAuditDto>(`/api/contacts/${contactId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  } catch (error) {
    logError(error, "updateContact");
    throw error;
  }
}

/**
 * Deletes a contact by its ID.
 * @param contactId - The ID of the contact to delete
 * @returns A promise that resolves when the contact is deleted.
 * @throws An error if the request fails or if the contact is not found.
 */
export async function deleteContact(contactId: string): Promise<void> {
  try {
    await apiClient<void>(`/api/contacts/${contactId}`, {
      method: "DELETE",
    });
  } catch (error) {
    logError(error, "deleteContact");
    throw error;
  }
}

/**
 * Fetches all contacts across all accounts for global dashboard statistics.
 * This aggregates contacts from all accounts the user has access to.
 *
 * @returns A list of all contacts across all accounts.
 * @throws An error if the request fails.
 */
export async function fetchAllContacts(): Promise<ContactDto[]> {
  try {
    // Fetch all contacts across all accounts in a single request
    return await apiClient<ContactDto[]>("/api/contacts/all", {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchAllContacts");
    throw error;
  }
}
